import camelCase from 'lodash/camelCase';
import isEmpty from 'lodash/isEmpty';

// STATEDIST-11171 (backend) now owns the seating-arrangement rows: when
// room_setup_redesign_enabled is on, GET /seatings returns exactly 2 rows — "Rows &
// columns" and this one. The backend deliberately never hardcodes this row's id (ids
// aren't portable across environments), only its name is stable, so this module never
// hardcodes an id for it either — everything here matches by name against the real
// /seatings response instead.
export const STADIUM_MULTI_LEVEL_SEATING_NAME = 'Stadium / Multi-level';

// STATEDIST-11640: replaces the STATEDIST-11172 placeholder grouping (COMBINED_SEATING_GROUPS)
// now that STATEDIST-11171 returns the 2 real rows directly — no client-side grouping/matching
// is needed any more. This table only drives the display-only spacing badge and is keyed by the
// same camelCase key transformOptions derives from each option's name, so it stays in sync with
// whatever /seatings returns. Shared with STATEDIST-11558/-11564 per the feature's cross-cutting
// concerns ("whichever story lands the shared lookup first, the others must reuse it").
export const SPACING_FT_BY_SEATING_KEY = {
  rowsColumns: 3,
  [camelCase(STADIUM_MULTI_LEVEL_SEATING_NAME)]: 5,
};

// Purely cosmetic icon glyphs (see shared/components/shape-select/shapes.jsx) for each
// seating-arrangement tile, keyed by the same camelCase key transformOptions derives from
// an option's name. This is UI-only and has nothing to do with the backend id — if
// /seatings ever returns a row whose key isn't listed here, ShapeSelect simply renders no
// icon for that tile rather than failing. Shared between STATEDIST-11172's RoomStructure
// screen and STATEDIST-11564's ModalSeatingDetails/SeatingDetails popup so both tile
// selectors render identically rather than maintaining two copies of this table.
export const SHAPE_ICON_KEYS_BY_SEATING_KEY = {
  rowsColumns: ['rowsColumns'],
  stadiumMultiLevel: ['stadiumSeating', 'multipleLevelSeating'],
};

// Mirrors the backend's SeatingFallbackMap (STATEDIST-11171): legacy seating ids 2
// ("Stadium seating") and 3 ("Multiple level seating") collapse into the new
// "Stadium / Multi-level" row; legacy id 4 ("Single level seating") collapses into "Rows &
// columns". This structural mapping (which legacy id maps to which canonical concept) is
// stable business logic and safe to hardcode. What must NOT be hardcoded is the numeric id
// assigned to "Stadium / Multi-level" in a given environment — that's resolved by name
// against the live /seatings response in resolveSeating() below.
const LEGACY_SEATING_ID_TO_CANONICAL_NAME = {
  2: STADIUM_MULTI_LEVEL_SEATING_NAME,
  3: STADIUM_MULTI_LEVEL_SEATING_NAME,
  4: 'Rows & columns',
};

export const transformOptions = (arr) =>
  arr.map((item) => {
    const i = { ...item };
    i.key = camelCase(item.name);
    return i;
  });

// Persisted `room.seating` (from the API) never carries the client-derived `key` field, since
// that's only added by transformOptions. Resolution proceeds in three steps:
//   1. Direct id match against the current /seatings options (covers rooms already saved
//      against the new 2-row response).
//   2. A legacy seating_id (2, 3, or 4, predating STATEDIST-11171) is mapped to its canonical
//      row *name* via LEGACY_SEATING_ID_TO_CANONICAL_NAME, then matched against the live
//      options list by that name (compared case/whitespace-insensitively via camelCase, so
//      display-string drift between the backend and this table doesn't silently break the
//      match) — never by assuming a fixed id for the canonical row.
//   3. If the option is missing entirely (e.g. removed), fall back to deriving the key
//      directly so the screen doesn't crash, even though it won't render as selected.
export const resolveSeating = (seatingOptions, persistedSeating) => {
  if (isEmpty(persistedSeating)) {
    return null;
  }

  const byId = seatingOptions.find((option) => option.id === persistedSeating.id);
  if (byId) {
    return byId;
  }

  const canonicalName = LEGACY_SEATING_ID_TO_CANONICAL_NAME[persistedSeating.id];
  const byCanonicalName =
    canonicalName && seatingOptions.find((option) => camelCase(option.name) === camelCase(canonicalName));
  if (byCanonicalName) {
    return byCanonicalName;
  }

  return { ...persistedSeating, key: camelCase(persistedSeating.name) };
};
